import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { EMPTY_FILTER_TOKEN, useInsightsParams } from '@insights_module/hooks/useInsightsParams';
import { CloseIcon } from '@icons/CloseIcon';
import { useSegmentCreatorContext } from '@insights_module/context/SegmentCreatorContext';
import { useViewsContext } from '@insights_module/context/ViewsContext';
import { EyeIcon } from '@icons/EyeIcon';
import { useViews } from '@insights_module/hooks/useViews';
import { FEATURE_FLAG, useFeatureFlag } from '@auth_module/hooks/useFeatureFlag';
import { useFilterTypeOptions } from '@insights_module/hooks/useFilterTypeOptions';

const Separator = tw.div`h-[20px] w-[1px] bg-gray-3`;
const QueryFilterLabel = tw.div`flex items-center justify-between text-xs px-2 h-[24px] hover:opacity-75 transition-all duration-100 bg-gray-2 rounded whitespace-nowrap w-auto font-semibold p-1 pl-2 cursor-pointer gap-1.5`;
const AppliedFiltersContainer = tw.div`flex items-center justify-center h-[18px] px-[5px] py-0.5 bg-gray-2 rounded-sm text-xs font-semibold text-text-and-icon-muted`;
const CloseButton = tw.div`flex items-center justify-center w-[9px] h-[9px] ml-[4px]`;

export const AppliedFilters = () => {
  const { isEnabled: isIncludeUnprocessedEnabled } = useFeatureFlag(
    FEATURE_FLAG.INCLUDE_UNPROCESSED
  );
  const { t } = useTranslation();
  const { setQueryToken, isEmptyQueryToken, isDefaultQueryToken } = useInsightsParams();

  const { setShowFiltersModal } = useInsightsContext();
  const { segmentsData } = useSegmentCreatorContext();
  const { selectedView, setSelectedView, isViewChanged } = useViewsContext();
  const { getViewById, updateView } = useViews();
  const currentView = getViewById(selectedView as string);
  const { options } = useFilterTypeOptions();

  const handleRemoveQueryFilter = (e: MouseEvent) => {
    e.stopPropagation();
    setQueryToken(isIncludeUnprocessedEnabled ? EMPTY_FILTER_TOKEN : undefined);
    setSelectedView(null);

    if (selectedView) {
      updateView({ id: selectedView, payload: { default: false } });
    }
  };

  const numberOfFiltersApplied = segmentsData.filter((segment) =>
    options.includes(segment.type)
  ).length;

  if (
    (isIncludeUnprocessedEnabled && isEmptyQueryToken) ||
    (!isIncludeUnprocessedEnabled && isDefaultQueryToken) ||
    !numberOfFiltersApplied
  )
    return null;

  return (
    <>
      <Separator />
      <QueryFilterLabel onClick={() => setShowFiltersModal(true)}>
        {currentView ? (
          <>
            <EyeIcon tw='stroke-text-and-icon fill-none' />
            {currentView.name}
          </>
        ) : (
          t('insights.query_builder.applied_filter_label')
        )}
        <AppliedFiltersContainer css={[isViewChanged && tw`bg-accent-2 text-white`]}>
          {numberOfFiltersApplied}
        </AppliedFiltersContainer>
        <CloseButton onClick={(e) => handleRemoveQueryFilter(e as unknown as MouseEvent)}>
          <CloseIcon tw='fill-text-and-icon cursor-pointer w-2.5 h-2.5' />
        </CloseButton>
      </QueryFilterLabel>
    </>
  );
};
